// ─── Skill Graph ─────────────────────────────────────────────────────────────
// A prerequisite DAG of atomic learning skills derived from activity
// learningObjectives across all 14 subject JSON files (~502 activities).
//
// The graph is intentionally small (nodes, not leaves): each node represents
// a cluster of similar learningObjectives. Activity→skill mapping is produced
// by keyword-based auto-tagging (see `tagActivity`) so that all ~500 activities
// can be mapped without hand-labeling each one.
//
// Used by the adaptive recommender to:
//   • suggest next activities (getNextActivities)
//   • backchain a learning path to a target skill (getSkillPath)
//   • gate content by mastery (getUnlockedActivities)
// ─────────────────────────────────────────────────────────────────────────────

export type SkillSubject =
  | 'math'
  | 'literacy'
  | 'science'
  | 'language'
  | 'coding'
  | 'engineering'
  | 'financial'
  | 'eq'
  | 'creative'
  | 'visual-arts'
  | 'social-studies'
  | 'physical'
  | 'body-wellness';

export type TierBand = 'pre-k' | 'kinder' | 'early-elementary' | 'upper-elementary';

export interface SkillNode {
  id: string;
  subject: SkillSubject;
  name: string;
  description: string;
  /** Lowest tier at which this skill typically appears. */
  tierBand: TierBand;
  /** Keywords used by the auto-tagger to map learningObjectives → this skill. */
  keywords: string[];
}

export interface SkillEdge {
  /** Prerequisite skill id (must be mastered first). */
  from: string;
  /** Dependent skill id (unlocked by `from`). */
  to: string;
}

export interface SkillGraph {
  nodes: SkillNode[];
  edges: SkillEdge[];
  /** Activity id → list of skill ids this activity builds. Populated lazily. */
  activityToSkills: Record<string, string[]>;
}

// ─── Nodes ───────────────────────────────────────────────────────────────────
// Naming: <subject>.<thread>.<level>
// Threads are topical progressions within a subject (number, geometry, phonics, …)

const NODES: SkillNode[] = [
  // ── MATH: number & counting ────────────────────────────────────────────────
  {
    id: 'math.number.count-10',
    subject: 'math',
    name: 'Count to 10',
    description: 'One-to-one correspondence counting objects up to 10.',
    tierBand: 'pre-k',
    keywords: ['count objects up to 10', 'count to 10', 'one-to-one correspondence', 'count up to 10'],
  },
  {
    id: 'math.number.count-20',
    subject: 'math',
    name: 'Count to 20',
    description: 'Count, write, and compare quantities up to 20.',
    tierBand: 'kinder',
    keywords: ['count and record quantities up to 20', 'count to 20', 'quantities up to 20', 'numbers up to 20'],
  },
  {
    id: 'math.number.count-100',
    subject: 'math',
    name: 'Count to 100',
    description: 'Count, read, and write numbers up to 100; skip-counting.',
    tierBand: 'early-elementary',
    keywords: ['skip-count', 'skip count', 'count to 100', 'numbers to 100', 'place value'],
  },
  {
    id: 'math.number.place-value',
    subject: 'math',
    name: 'Place value (tens/hundreds)',
    description: 'Understand tens/ones, then hundreds, and compare multi-digit numbers.',
    tierBand: 'early-elementary',
    keywords: ['place value', 'tens and ones', 'hundreds', 'expanded form'],
  },
  {
    id: 'math.number.compare',
    subject: 'math',
    name: 'Compare quantities',
    description: 'Compare groups using most/fewest/equal and greater/less than.',
    tierBand: 'pre-k',
    keywords: ['compare groups', 'most, fewest', 'greater than', 'less than', 'compare quantities'],
  },
  // ── MATH: operations ───────────────────────────────────────────────────────
  {
    id: 'math.ops.add-sub-small',
    subject: 'math',
    name: 'Add & subtract within 10',
    description: 'Single-digit addition and subtraction with concrete objects.',
    tierBand: 'kinder',
    keywords: ['add within 10', 'addition', 'subtract', 'sum', 'number sentence'],
  },
  {
    id: 'math.ops.add-sub-100',
    subject: 'math',
    name: 'Add & subtract within 100',
    description: 'Multi-digit addition and subtraction with regrouping.',
    tierBand: 'early-elementary',
    keywords: ['two-digit addition', 'regrouping', 'add and subtract', 'multi-digit'],
  },
  {
    id: 'math.ops.multiplication',
    subject: 'math',
    name: 'Multiplication',
    description: 'Repeated addition, arrays, multiplication facts.',
    tierBand: 'early-elementary',
    keywords: ['multiplication', 'multiply', 'times table', 'array', 'repeated addition'],
  },
  {
    id: 'math.ops.division',
    subject: 'math',
    name: 'Division',
    description: 'Partitive and quotative division; remainders.',
    tierBand: 'upper-elementary',
    keywords: ['division', 'divide', 'quotient', 'remainder', 'share equally'],
  },
  {
    id: 'math.ops.ratio',
    subject: 'math',
    name: 'Ratios & proportions',
    description: 'Part-to-part and part-to-whole ratios, simple proportions.',
    tierBand: 'upper-elementary',
    keywords: ['ratio', 'proportion', 'scale factor', '3-to-1', 'for every'],
  },
  // ── MATH: fractions & decimals ─────────────────────────────────────────────
  {
    id: 'math.fractions.halves-quarters',
    subject: 'math',
    name: 'Halves and quarters',
    description: 'Recognize equal parts of a whole.',
    tierBand: 'early-elementary',
    keywords: ['halves', 'quarters', 'equal parts', 'half of'],
  },
  {
    id: 'math.fractions.equivalent',
    subject: 'math',
    name: 'Equivalent fractions',
    description: 'Compare fractions and identify equivalent forms.',
    tierBand: 'upper-elementary',
    keywords: ['equivalent fraction', 'compare fractions', 'fraction', 'numerator'],
  },
  {
    id: 'math.fractions.decimals',
    subject: 'math',
    name: 'Decimals & percents',
    description: 'Read, compare, and convert decimals and percents.',
    tierBand: 'upper-elementary',
    keywords: ['decimal', 'percent', 'percentage', 'tenths', 'hundredths'],
  },
  // ── MATH: geometry & measurement ───────────────────────────────────────────
  {
    id: 'math.geom.shapes',
    subject: 'math',
    name: 'Identify 2D shapes',
    description: 'Name and describe circles, squares, triangles, rectangles.',
    tierBand: 'pre-k',
    keywords: ['identify shapes', '2d shapes', 'circle', 'triangle', 'rectangle', 'shape recognition'],
  },
  {
    id: 'math.geom.solids',
    subject: 'math',
    name: '3D shapes',
    description: 'Identify and describe cubes, spheres, cones, cylinders.',
    tierBand: 'kinder',
    keywords: ['3d shapes', 'cube', 'sphere', 'cone', 'cylinder', 'solid shape'],
  },
  {
    id: 'math.geom.symmetry',
    subject: 'math',
    name: 'Symmetry & transformations',
    description: 'Lines of symmetry, flips, slides, turns.',
    tierBand: 'early-elementary',
    keywords: ['symmetry', 'symmetric', 'reflection', 'rotation', 'mirror'],
  },
  {
    id: 'math.geom.area-perimeter',
    subject: 'math',
    name: 'Area & perimeter',
    description: 'Measure and calculate area and perimeter of rectangles.',
    tierBand: 'upper-elementary',
    keywords: ['area', 'perimeter', 'square units'],
  },
  {
    id: 'math.measure.length',
    subject: 'math',
    name: 'Measure length',
    description: 'Use rulers and non-standard units to measure.',
    tierBand: 'kinder',
    keywords: ['measure length', 'ruler', 'inches', 'centimeters', 'measure with'],
  },
  {
    id: 'math.measure.time',
    subject: 'math',
    name: 'Time & calendar',
    description: 'Read clocks, understand days/weeks/months.',
    tierBand: 'early-elementary',
    keywords: ['tell time', 'clock', 'calendar', 'elapsed time', 'minutes and hours'],
  },
  // ── MATH: data & reasoning ─────────────────────────────────────────────────
  {
    id: 'math.data.sort-classify',
    subject: 'math',
    name: 'Sort and classify',
    description: 'Group objects by a single attribute.',
    tierBand: 'pre-k',
    keywords: ['sort', 'classify', 'group by', 'categorize objects'],
  },
  {
    id: 'math.data.tally-graph',
    subject: 'math',
    name: 'Tally charts & bar graphs',
    description: 'Collect data, tally, and make simple bar graphs.',
    tierBand: 'kinder',
    keywords: ['tally chart', 'bar graph', 'pictograph', 'collect data', 'record data'],
  },
  {
    id: 'math.data.statistics',
    subject: 'math',
    name: 'Mean, median, mode',
    description: 'Summarize data with measures of center.',
    tierBand: 'upper-elementary',
    keywords: ['mean', 'median', 'mode', 'average', 'measures of center'],
  },
  {
    id: 'math.patterns.basic',
    subject: 'math',
    name: 'Patterns (AB, ABC)',
    description: 'Recognize and extend repeating patterns.',
    tierBand: 'pre-k',
    keywords: ['pattern', 'repeating pattern', 'extend a pattern', 'ab pattern'],
  },
  {
    id: 'math.patterns.function',
    subject: 'math',
    name: 'Function rules & variables',
    description: 'Identify a rule, use variables to express it.',
    tierBand: 'upper-elementary',
    keywords: ['function rule', 'variable', 'expression', 'input/output'],
  },

  // ── LITERACY: phonological ─────────────────────────────────────────────────
  {
    id: 'lit.phono.rhyme',
    subject: 'literacy',
    name: 'Rhyme awareness',
    description: 'Recognize and produce rhyming words.',
    tierBand: 'pre-k',
    keywords: ['rhyme', 'rhyming words'],
  },
  {
    id: 'lit.phono.initial-sounds',
    subject: 'literacy',
    name: 'Initial sounds',
    description: 'Identify the first sound in spoken words.',
    tierBand: 'pre-k',
    keywords: ['beginning letter sounds', 'initial sound', 'starting sound', 'first sound'],
  },
  {
    id: 'lit.phono.blend-segment',
    subject: 'literacy',
    name: 'Blend & segment sounds',
    description: 'Blend phonemes into words and segment words into phonemes.',
    tierBand: 'kinder',
    keywords: ['blend sounds', 'segment', 'phoneme', 'sound out'],
  },
  // ── LITERACY: decoding ─────────────────────────────────────────────────────
  {
    id: 'lit.decode.letters',
    subject: 'literacy',
    name: 'Letter recognition',
    description: 'Name uppercase and lowercase letters.',
    tierBand: 'pre-k',
    keywords: ['recognize', 'uppercase letters', 'lowercase', 'letter names', 'name letters', 'alphabet'],
  },
  {
    id: 'lit.decode.letter-sounds',
    subject: 'literacy',
    name: 'Letter–sound correspondence',
    description: 'Match all 26 letters to their primary sounds.',
    tierBand: 'pre-k',
    keywords: ['letter sounds', 'letter-sound', '26 letters', 'letters with their sounds'],
  },
  {
    id: 'lit.decode.cvc',
    subject: 'literacy',
    name: 'Decode CVC words',
    description: 'Read consonant-vowel-consonant words.',
    tierBand: 'kinder',
    keywords: ['cvc', 'consonant-vowel-consonant', 'sound out simple words', 'decode'],
  },
  {
    id: 'lit.decode.sight-words',
    subject: 'literacy',
    name: 'High-frequency / sight words',
    description: 'Recognize common sight words automatically.',
    tierBand: 'kinder',
    keywords: ['sight word', 'high-frequency word'],
  },
  {
    id: 'lit.decode.fluency',
    subject: 'literacy',
    name: 'Reading fluency',
    description: 'Read connected text with accuracy, rate, and expression.',
    tierBand: 'early-elementary',
    keywords: ['read aloud', 'fluency', 'expression', 'prosody'],
  },
  // ── LITERACY: comprehension & writing ──────────────────────────────────────
  {
    id: 'lit.comp.retell',
    subject: 'literacy',
    name: 'Retell & sequence a story',
    description: 'Recall events of a story in order.',
    tierBand: 'pre-k',
    keywords: ['retell', 'sequence events', 'story order', 'beginning middle end'],
  },
  {
    id: 'lit.comp.main-idea',
    subject: 'literacy',
    name: 'Main idea & details',
    description: 'Identify main idea and supporting details.',
    tierBand: 'early-elementary',
    keywords: ['main idea', 'supporting detail', 'key details'],
  },
  {
    id: 'lit.comp.inference',
    subject: 'literacy',
    name: 'Inference',
    description: 'Use text evidence to infer meaning beyond the literal.',
    tierBand: 'upper-elementary',
    keywords: ['inference', 'infer', 'text evidence', 'draw conclusions'],
  },
  {
    id: 'lit.write.letters',
    subject: 'literacy',
    name: 'Letter formation',
    description: 'Form letters correctly with proper strokes.',
    tierBand: 'pre-k',
    keywords: ['letter formation', 'trace letters', 'write letters', 'handwriting'],
  },
  {
    id: 'lit.write.sentences',
    subject: 'literacy',
    name: 'Write complete sentences',
    description: 'Compose sentences with capitalization and end punctuation.',
    tierBand: 'kinder',
    keywords: ['complete sentence', 'capitalization', 'punctuation', 'write sentences'],
  },
  {
    id: 'lit.write.paragraph',
    subject: 'literacy',
    name: 'Paragraph writing',
    description: 'Organize multiple sentences around a topic.',
    tierBand: 'early-elementary',
    keywords: ['paragraph', 'topic sentence', 'organize writing'],
  },
  {
    id: 'lit.write.narrative',
    subject: 'literacy',
    name: 'Narrative writing',
    description: 'Write a story with setting, character, and plot.',
    tierBand: 'early-elementary',
    keywords: ['narrative', 'write a story', 'plot', 'character', 'setting'],
  },
  {
    id: 'lit.write.informational',
    subject: 'literacy',
    name: 'Informational / analytical writing',
    description: 'Write reports and analysis supported by evidence.',
    tierBand: 'upper-elementary',
    keywords: ['analysis report', 'informational', 'research report', 'analytical', 'write analytical'],
  },
  {
    id: 'lit.vocab.descriptive',
    subject: 'literacy',
    name: 'Descriptive vocabulary',
    description: 'Use precise adjectives, similes, and sensory words.',
    tierBand: 'early-elementary',
    keywords: ['descriptive language', 'adjective', 'simile', 'sensory', 'precise word'],
  },

  // ── SCIENCE: inquiry ───────────────────────────────────────────────────────
  {
    id: 'sci.inq.observe',
    subject: 'science',
    name: 'Sensory observation',
    description: 'Describe what you see, hear, feel, smell, taste.',
    tierBand: 'pre-k',
    keywords: ['scientific observation', 'observe and describe', 'sensory language', 'five senses', 'observe using'],
  },
  {
    id: 'sci.inq.predict',
    subject: 'science',
    name: 'Predict & hypothesize',
    description: 'Make predictions before an experiment.',
    tierBand: 'kinder',
    keywords: ['predict', 'prediction', 'hypothesis', 'hypothesize'],
  },
  {
    id: 'sci.inq.record',
    subject: 'science',
    name: 'Record observations',
    description: 'Use drawings, tables, and notes to record findings.',
    tierBand: 'kinder',
    keywords: ['record observations', 'record data', 'draw observations', 'science journal'],
  },
  {
    id: 'sci.inq.variable',
    subject: 'science',
    name: 'Controlled experiments',
    description: 'Change one variable at a time and compare results.',
    tierBand: 'early-elementary',
    keywords: ['controlled experiment', 'one variable', 'variable at a time', 'test variables'],
  },
  {
    id: 'sci.inq.analyze',
    subject: 'science',
    name: 'Analyze & conclude',
    description: 'Draw evidence-based conclusions from data.',
    tierBand: 'upper-elementary',
    keywords: ['analytical conclusions', 'evidence', 'draw conclusions', 'interpret data'],
  },
  // ── SCIENCE: physical ─────────────────────────────────────────────────────
  {
    id: 'sci.phys.states-of-matter',
    subject: 'science',
    name: 'States of matter',
    description: 'Solids, liquids, gases and phase change.',
    tierBand: 'kinder',
    keywords: ['states of matter', 'solid liquid gas', 'melting', 'freezing', 'phase change'],
  },
  {
    id: 'sci.phys.reactions',
    subject: 'science',
    name: 'Chemical reactions',
    description: 'Mixing materials to create new results.',
    tierBand: 'pre-k',
    keywords: ['chemical reaction', 'mixing materials', 'acid and base', 'fizz'],
  },
  {
    id: 'sci.phys.forces',
    subject: 'science',
    name: 'Forces & motion',
    description: 'Push, pull, gravity, friction.',
    tierBand: 'early-elementary',
    keywords: ['force', 'motion', 'gravity', 'friction', 'push and pull'],
  },
  {
    id: 'sci.phys.energy',
    subject: 'science',
    name: 'Energy & electricity',
    description: 'Light, heat, sound, simple circuits.',
    tierBand: 'upper-elementary',
    keywords: ['energy', 'circuit', 'electricity', 'light energy', 'sound wave'],
  },
  // ── SCIENCE: life ──────────────────────────────────────────────────────────
  {
    id: 'sci.life.living-things',
    subject: 'science',
    name: 'Living vs non-living',
    description: 'Recognize characteristics of living things.',
    tierBand: 'pre-k',
    keywords: ['living thing', 'non-living', 'alive', 'needs of living'],
  },
  {
    id: 'sci.life.plants',
    subject: 'science',
    name: 'Plants & growth',
    description: 'Plant parts, life cycle, photosynthesis.',
    tierBand: 'kinder',
    keywords: ['plant', 'seed', 'photosynthesis', 'life cycle of a plant', 'root stem leaf'],
  },
  {
    id: 'sci.life.animals',
    subject: 'science',
    name: 'Animals & habitats',
    description: 'Classify animals and understand habitats.',
    tierBand: 'pre-k',
    keywords: ['habitat', 'classify animal', 'animal group', 'mammal', 'reptile'],
  },
  {
    id: 'sci.life.ecosystems',
    subject: 'science',
    name: 'Ecosystems & food webs',
    description: 'Producers, consumers, decomposers and energy flow.',
    tierBand: 'upper-elementary',
    keywords: ['ecosystem', 'food web', 'food chain', 'producer', 'consumer'],
  },
  // ── SCIENCE: earth & space ─────────────────────────────────────────────────
  {
    id: 'sci.earth.weather',
    subject: 'science',
    name: 'Weather patterns',
    description: 'Observe and describe weather and seasons.',
    tierBand: 'pre-k',
    keywords: ['weather', 'seasons', 'temperature', 'precipitation'],
  },
  {
    id: 'sci.earth.water-cycle',
    subject: 'science',
    name: 'Water cycle',
    description: 'Evaporation, condensation, precipitation.',
    tierBand: 'early-elementary',
    keywords: ['water cycle', 'evaporation', 'condensation', 'precipitation'],
  },
  {
    id: 'sci.earth.rocks-geology',
    subject: 'science',
    name: 'Rocks & geology',
    description: 'Rock types, erosion, earth layers.',
    tierBand: 'early-elementary',
    keywords: ['rock', 'mineral', 'erosion', 'geology', 'sediment'],
  },
  {
    id: 'sci.space.solar-system',
    subject: 'science',
    name: 'Solar system',
    description: 'Sun, planets, moon phases.',
    tierBand: 'early-elementary',
    keywords: ['planet', 'solar system', 'moon phase', 'orbit', 'star'],
  },

  // ── LANGUAGE (foreign / ELA overlap) ───────────────────────────────────────
  {
    id: 'lang.vocab.basic',
    subject: 'language',
    name: 'Basic vocabulary building',
    description: 'Learn and use new words through repetition.',
    tierBand: 'pre-k',
    keywords: ['build vocabulary', 'new vocabulary', 'word bank', 'learn words'],
  },
  {
    id: 'lang.vocab.categorize',
    subject: 'language',
    name: 'Categorize vocabulary',
    description: 'Group words by meaning or theme.',
    tierBand: 'kinder',
    keywords: ['categorize', 'sort vocabulary', 'word category', 'organize words'],
  },
  {
    id: 'lang.grammar.sentence',
    subject: 'language',
    name: 'Sentence construction',
    description: 'Build grammatically complete sentences.',
    tierBand: 'early-elementary',
    keywords: ['complete descriptive sentence', 'sentence structure', 'subject and verb'],
  },

  // ── CODING / COMPUTATIONAL THINKING ───────────────────────────────────────
  {
    id: 'code.seq.instructions',
    subject: 'coding',
    name: 'Sequential instructions',
    description: 'Break a task into ordered steps.',
    tierBand: 'pre-k',
    keywords: ['sequential instruction', 'step-by-step', 'break tasks into', 'ordered steps'],
  },
  {
    id: 'code.seq.algorithms',
    subject: 'coding',
    name: 'Algorithms',
    description: 'Design algorithms to solve problems.',
    tierBand: 'early-elementary',
    keywords: ['algorithm', 'solve problems', 'procedure'],
  },
  {
    id: 'code.debug',
    subject: 'coding',
    name: 'Debugging',
    description: 'Find and fix errors in a program.',
    tierBand: 'kinder',
    keywords: ['debug', 'fix incorrect', 'find errors', 'fix a program'],
  },
  {
    id: 'code.loops',
    subject: 'coding',
    name: 'Loops / iteration',
    description: 'Use repeat blocks to reduce duplication.',
    tierBand: 'early-elementary',
    keywords: ['loop', 'repeat block', 'iteration'],
  },
  {
    id: 'code.conditionals',
    subject: 'coding',
    name: 'Conditionals',
    description: 'Use if/then logic to branch behavior.',
    tierBand: 'early-elementary',
    keywords: ['conditional', 'if then', 'if/then', 'branching logic'],
  },
  {
    id: 'code.variables',
    subject: 'coding',
    name: 'Variables & data',
    description: 'Store and update values with variables.',
    tierBand: 'upper-elementary',
    keywords: ['variable', 'store value', 'counter variable'],
  },
  {
    id: 'code.functions',
    subject: 'coding',
    name: 'Functions / procedures',
    description: 'Group steps into reusable named procedures.',
    tierBand: 'upper-elementary',
    keywords: ['function', 'procedure', 'reusable block', 'define a function'],
  },

  // ── ENGINEERING / DESIGN ───────────────────────────────────────────────────
  {
    id: 'eng.design.build',
    subject: 'engineering',
    name: 'Build & balance',
    description: 'Explore support, balance, and stacking.',
    tierBand: 'pre-k',
    keywords: ['support and balance', 'stack', 'structural concept', 'build structure'],
  },
  {
    id: 'eng.design.plan',
    subject: 'engineering',
    name: 'Plan before building',
    description: 'Sketch a blueprint or plan before construction.',
    tierBand: 'kinder',
    keywords: ['blueprint', 'plan a design', 'plan before', 'sketch before'],
  },
  {
    id: 'eng.design.iterate',
    subject: 'engineering',
    name: 'Iterative design',
    description: 'Build, test, improve cycle.',
    tierBand: 'kinder',
    keywords: ['iterative design', 'build test improve', 'redesign', 'test and improve'],
  },
  {
    id: 'eng.design.process',
    subject: 'engineering',
    name: 'Engineering design process',
    description: 'Full cycle: define → design → build → test → iterate.',
    tierBand: 'early-elementary',
    keywords: ['engineering design process', 'problem definition to testing', 'design cycle'],
  },
  {
    id: 'eng.design.constraints',
    subject: 'engineering',
    name: 'Work within constraints',
    description: 'Optimize within material/budget/time limits.',
    tierBand: 'upper-elementary',
    keywords: ['constraint', 'material constraint', 'optimize a design', 'budget'],
  },

  // ── FINANCIAL ──────────────────────────────────────────────────────────────
  {
    id: 'fin.money.items-cost',
    subject: 'financial',
    name: 'Items cost money',
    description: 'Understand exchange of money for goods.',
    tierBand: 'pre-k',
    keywords: ['items cost money', 'pay to receive', 'trade money for'],
  },
  {
    id: 'fin.money.coins',
    subject: 'financial',
    name: 'Identify and count coins',
    description: 'Penny, nickel, dime, quarter values.',
    tierBand: 'kinder',
    keywords: ['count coins', 'identify coins', 'penny nickel dime quarter'],
  },
  {
    id: 'fin.money.change',
    subject: 'financial',
    name: 'Make change',
    description: 'Compute change from a purchase.',
    tierBand: 'early-elementary',
    keywords: ['make change', 'exact change', 'give change'],
  },
  {
    id: 'fin.money.budget',
    subject: 'financial',
    name: 'Budgeting',
    description: 'Plan spending within an income limit.',
    tierBand: 'upper-elementary',
    keywords: ['budget', 'plan spending', 'income', 'expense'],
  },
  {
    id: 'fin.save-invest',
    subject: 'financial',
    name: 'Saving & investing',
    description: 'Delay gratification; compound growth concepts.',
    tierBand: 'upper-elementary',
    keywords: ['saving', 'investing', 'compound', 'interest', 'savings goal'],
  },
  {
    id: 'fin.entrepreneur',
    subject: 'financial',
    name: 'Entrepreneurship basics',
    description: 'Pricing, profit, customer value.',
    tierBand: 'early-elementary',
    keywords: ['entrepreneur', 'profit', 'price', 'business', 'sell'],
  },

  // ── EQ / SEL ───────────────────────────────────────────────────────────────
  {
    id: 'eq.emotions.name',
    subject: 'eq',
    name: 'Name emotions',
    description: 'Label basic feelings with specific words.',
    tierBand: 'pre-k',
    keywords: ['emotion words', 'feelings words', 'name feelings', 'emotion vocabulary'],
  },
  {
    id: 'eq.emotions.nuance',
    subject: 'eq',
    name: 'Emotional nuance',
    description: 'Distinguish between similar emotions.',
    tierBand: 'early-elementary',
    keywords: ['precise emotion', 'similar emotion', 'nuanced feeling', 'distinguish between'],
  },
  {
    id: 'eq.regulate.calm',
    subject: 'eq',
    name: 'Self-regulation strategies',
    description: 'Use breathing and pause tools to calm down.',
    tierBand: 'pre-k',
    keywords: ['calm down', 'deep breath', 'self-regulation', 'regulate', 'cool down'],
  },
  {
    id: 'eq.social.empathy',
    subject: 'eq',
    name: 'Empathy & perspective',
    description: 'Recognize others’ feelings; take their view.',
    tierBand: 'kinder',
    keywords: ['empathy', 'perspective', 'others feelings', 'put yourself in'],
  },
  {
    id: 'eq.social.conflict',
    subject: 'eq',
    name: 'Conflict resolution',
    description: 'Resolve disagreements respectfully.',
    tierBand: 'early-elementary',
    keywords: ['conflict resolution', 'resolve disagreement', 'i-statement', 'compromise'],
  },

  // ── CREATIVE / VISUAL ARTS ─────────────────────────────────────────────────
  {
    id: 'art.motor.fine',
    subject: 'creative',
    name: 'Fine motor control',
    description: 'Kneading, pinching, cutting, small-tool handling.',
    tierBand: 'pre-k',
    keywords: ['fine motor', 'kneading', 'pinching', 'rolling'],
  },
  {
    id: 'art.represent',
    subject: 'creative',
    name: 'Represent ideas in 3D',
    description: 'Use clay / materials to model real objects.',
    tierBand: 'pre-k',
    keywords: ['represent real objects', '3d form', 'sculpt'],
  },
  {
    id: 'art.composition',
    subject: 'visual-arts',
    name: 'Composition basics',
    description: 'Foreground/background; rule of thirds; framing.',
    tierBand: 'kinder',
    keywords: ['composition', 'foreground', 'rule of thirds', 'framing', 'leading lines'],
  },
  {
    id: 'art.project.plan',
    subject: 'creative',
    name: 'Plan a multi-step art project',
    description: 'Sequence multi-piece creative work.',
    tierBand: 'early-elementary',
    keywords: ['multi-piece creative project', 'multi-element', 'art project'],
  },

  // ── SOCIAL STUDIES ─────────────────────────────────────────────────────────
  {
    id: 'ss.maps.basic',
    subject: 'social-studies',
    name: 'Maps represent places',
    description: 'Understand bird’s-eye view and symbolic representation.',
    tierBand: 'pre-k',
    keywords: ['maps represent', 'bird\'s-eye view', "bird's-eye", 'symbol for', 'map of a room'],
  },
  {
    id: 'ss.maps.features',
    subject: 'social-studies',
    name: 'Map features & directions',
    description: 'Compass rose, key, cardinal directions.',
    tierBand: 'early-elementary',
    keywords: ['compass rose', 'cardinal direction', 'map key', 'north south east west'],
  },
  {
    id: 'ss.maps.scale',
    subject: 'social-studies',
    name: 'Scale & coordinates',
    description: 'Proportional distance and grid references.',
    tierBand: 'upper-elementary',
    keywords: ['distance scale', 'proportional spacing', 'coordinates', 'grid reference'],
  },
  {
    id: 'ss.community',
    subject: 'social-studies',
    name: 'Community & citizenship',
    description: 'Roles, rules, responsibilities.',
    tierBand: 'pre-k',
    keywords: ['community', 'citizen', 'rules and responsibilities', 'neighborhood'],
  },
  {
    id: 'ss.history.timeline',
    subject: 'social-studies',
    name: 'Timelines & chronology',
    description: 'Place events in time order.',
    tierBand: 'early-elementary',
    keywords: ['timeline', 'chronology', 'order of events', 'historical timeline'],
  },
  {
    id: 'ss.culture',
    subject: 'social-studies',
    name: 'Cultures & traditions',
    description: 'Recognize cultural practices around the world.',
    tierBand: 'kinder',
    keywords: ['culture', 'tradition', 'customs', 'around the world'],
  },
  {
    id: 'ss.geography.regions',
    subject: 'social-studies',
    name: 'Geography & regions',
    description: 'Continents, countries, climate regions.',
    tierBand: 'early-elementary',
    keywords: ['continent', 'country', 'climate region', 'geography'],
  },
  {
    id: 'ss.econ.trade',
    subject: 'social-studies',
    name: 'Goods, services & trade',
    description: 'How goods and services move between people.',
    tierBand: 'early-elementary',
    keywords: ['goods and services', 'trade', 'supply and demand', 'economy'],
  },

  // ── PHYSICAL / BODY-WELLNESS ───────────────────────────────────────────────
  {
    id: 'phys.motor.locomotor',
    subject: 'physical',
    name: 'Locomotor movements',
    description: 'Hopping, crawling, sliding, running.',
    tierBand: 'pre-k',
    keywords: ['locomotor', 'hopping', 'crawling', 'sliding', 'gross motor'],
  },
  {
    id: 'phys.motor.coordination',
    subject: 'physical',
    name: 'Coordination & sequencing',
    description: 'Combine movements in a sequence.',
    tierBand: 'kinder',
    keywords: ['combine movement', 'coordination', 'movement sequence'],
  },
  {
    id: 'phys.fitness.endurance',
    subject: 'physical',
    name: 'Endurance & goal-setting',
    description: 'Sustained activity; improve personal time.',
    tierBand: 'early-elementary',
    keywords: ['endurance', 'sustained physical', 'personal goal', 'improve time'],
  },
  {
    id: 'body.anatomy.parts',
    subject: 'body-wellness',
    name: 'Body parts (external)',
    description: 'Name external body parts.',
    tierBand: 'pre-k',
    keywords: ['external body part', 'body part', 'parts of the body'],
  },
  {
    id: 'body.anatomy.systems',
    subject: 'body-wellness',
    name: 'Body systems',
    description: 'Skeletal, muscular, circulatory basics.',
    tierBand: 'early-elementary',
    keywords: ['bones', 'joint', 'muscle', 'anatomical', 'body system', 'circulatory'],
  },
  {
    id: 'body.nutrition',
    subject: 'body-wellness',
    name: 'Nutrition basics',
    description: 'Food groups, healthy choices, hydration.',
    tierBand: 'kinder',
    keywords: ['food group', 'nutrition', 'healthy food', 'hydration', 'vitamin'],
  },
  {
    id: 'body.hygiene',
    subject: 'body-wellness',
    name: 'Hygiene & health habits',
    description: 'Handwashing, sleep, self-care routines.',
    tierBand: 'pre-k',
    keywords: ['hygiene', 'handwash', 'brush teeth', 'sleep habit'],
  },

  // ── Entrepreneurship subtree (Entrepreneur Fundamentals course) ──────────
  {
    id: 'fin.ent.wants-vs-needs',
    subject: 'financial',
    name: 'Wants vs. needs',
    description: 'Distinguish survival needs (food, water, shelter) from wants; apply needs-first spending order.',
    tierBand: 'kinder',
    keywords: ['wants vs needs', 'wants and needs', 'needs first', 'need or want', 'savings mindset', 'savings-mindset'],
  },
  {
    id: 'fin.ent.barter',
    subject: 'financial',
    name: 'Trade & barter basics',
    description: 'Understand exchange of goods/services without money; double coincidence of wants; history of money.',
    tierBand: 'kinder',
    keywords: ['barter', 'trade', 'exchange', 'exchange-barter', 'history of money', 'history-of-money', 'money invention'],
  },
  {
    id: 'fin.ent.value',
    subject: 'financial',
    name: 'Value: scarcity × demand',
    description: 'Value as the product of scarcity and demand; create value through differentiation, branding, rarity.',
    tierBand: 'early-elementary',
    keywords: ['value', 'scarcity', 'demand', 'value-scarcity-desire', 'perceived value', 'intrinsic value'],
  },
  {
    id: 'fin.ent.pricing',
    subject: 'financial',
    name: 'Pricing strategy',
    description: 'Set prices that balance customer willingness-to-pay and profit; A/B test; avoid cost-plus and underpricing traps.',
    tierBand: 'early-elementary',
    keywords: ['pricing strategy', 'pricing-strategy', 'price test', 'goldilocks price', 'price anchoring', 'charm pricing', 'veblen effect'],
  },
  {
    id: 'fin.ent.business-plan',
    subject: 'financial',
    name: 'Simple business plan',
    description: 'Write a one-page plan: cost sheet, price, break-even, location, signage, first customers, risks.',
    tierBand: 'early-elementary',
    keywords: ['business planning', 'business-planning', 'cost analysis', 'cost-analysis', 'profit-loss', 'break-even', 'cost sheet'],
  },
  {
    id: 'fin.ent.customer-service',
    subject: 'financial',
    name: 'Customer service & sales',
    description: 'Selling as helping; ask-listen-offer; eye contact, smile, listening; customer experience as competitive advantage.',
    tierBand: 'early-elementary',
    keywords: ['sales-serve-not-sell', 'customer-experience', 'customer service', 'selling is helping', 'ask listen offer', 'eq-confidence'],
  },
  {
    id: 'fin.ent.market-research',
    subject: 'financial',
    name: 'Market research & arbitrage',
    description: 'Research customer demand, competitor prices, and unevenly-priced micro-markets; spot arbitrage.',
    tierBand: 'upper-elementary',
    keywords: ['market research', 'market-research', 'arbitrage', 'margin-buy-low-sell-high', 'buy low sell high', 'risk-management'],
  },
  {
    id: 'fin.ent.reliability',
    subject: 'financial',
    name: 'Reliability & customer retention',
    description: 'Show up consistently; build reputation; turn one-time customers into repeat customers; ask for referrals.',
    tierBand: 'early-elementary',
    keywords: ['work-reliability', 'reputation', 'discipline-consistency', 'customer-retention', 'word-of-mouth', 'referral-asking', 'customer-lifetime-value'],
  },
  {
    id: 'fin.ent.services-pricing',
    subject: 'financial',
    name: 'Service-business pricing',
    description: 'Price services by hourly rate, variable costs, and equipment amortization; spot hidden expenses.',
    tierBand: 'upper-elementary',
    keywords: ['services-price-your-time', 'costs-hidden-expenses', 'service business', 'amortization'],
  },
  {
    id: 'fin.ent.diversification',
    subject: 'financial',
    name: 'Seasonality & diversification',
    description: 'Pair seasonal services; smooth income across feast/famine cycles; basic diversification thinking.',
    tierBand: 'upper-elementary',
    keywords: ['seasonality-plan-ahead', 'diversification-intro', 'income-smoothing', 'seasonal'],
  },
  {
    id: 'fin.ent.trades-careers',
    subject: 'financial',
    name: 'Trades & automation-resistant careers',
    description: 'Recognize skilled trades (plumbing, electrical, HVAC, welding) as high-paying, automation-resistant careers; understand apprenticeships.',
    tierBand: 'upper-elementary',
    keywords: ['trades-awareness', 'career-options', 'automation-resilience', 'apprenticeship', 'skilled trades'],
  },
  {
    id: 'fin.ent.bookkeeping',
    subject: 'financial',
    name: 'Simple bookkeeping & cash flow',
    description: 'Maintain a 4-column ledger; track income vs. expenses; understand cash flow vs. profit.',
    tierBand: 'upper-elementary',
    keywords: ['accounting-simple-ledger', 'bookkeeping', 'cash-flow', 'ledger'],
  },
  {
    id: 'fin.ent.scale',
    subject: 'financial',
    name: 'Capacity scaling & reinvestment',
    description: 'Climb the capacity ladder (bike → car → van → fleet); reinvest profits to upgrade equipment and expand reach.',
    tierBand: 'upper-elementary',
    keywords: ['scale-capacity-ladder', 'growth-arc-thinking', 'capital-reinvestment', 'capacity ladder'],
  },
  {
    id: 'fin.ent.delegation',
    subject: 'financial',
    name: 'Hiring & delegation',
    description: 'Hire fairly (60-70% to worker); transition from worker to owner; address quality, training, and accountability.',
    tierBand: 'upper-elementary',
    keywords: ['team-first-hire', 'delegation-intro', 'owner-vs-worker', 'hiring'],
  },
  {
    id: 'fin.ent.problem-spotting',
    subject: 'financial',
    name: 'Problem-spotting & pattern recognition',
    description: 'Identify problems people will pay to solve; recognize patterns across complaints; apply Five Whys.',
    tierBand: 'upper-elementary',
    keywords: ['problem-spotting', 'pattern-recognition', 'opportunity-identification', 'five whys'],
  },
  {
    id: 'fin.ent.platform-skepticism',
    subject: 'financial',
    name: 'Online platforms & honest skepticism',
    description: 'Understand modern online business channels (YouTube, Etsy, TikTok, freelancing); skepticism toward MLM/dropshipping/crypto pitches.',
    tierBand: 'upper-elementary',
    keywords: ['platforms-modern-channels', 'skepticism-hype-vs-reality', 'online business', 'MLM', 'dropshipping'],
  },
  {
    id: 'fin.ent.pitch',
    subject: 'financial',
    name: 'Pitch & synthesis',
    description: 'Synthesize a one-page plan; deliver a 2-minute pitch with problem-solution-numbers-ask structure.',
    tierBand: 'upper-elementary',
    keywords: ['pitch-presentation', 'synthesis', 'one page plan', 'demo day pitch'],
  },
];

// ─── Edges ───────────────────────────────────────────────────────────────────
// Prerequisite → Dependent. Kept close to pedagogical common sense; can be
// tuned without touching consumers.

const EDGES: SkillEdge[] = [
  // Math: number thread
  { from: 'math.number.count-10', to: 'math.number.count-20' },
  { from: 'math.number.count-20', to: 'math.number.count-100' },
  { from: 'math.number.count-100', to: 'math.number.place-value' },
  { from: 'math.number.count-10', to: 'math.number.compare' },
  { from: 'math.number.compare', to: 'math.ops.add-sub-small' },
  // Math: operations
  { from: 'math.number.count-20', to: 'math.ops.add-sub-small' },
  { from: 'math.ops.add-sub-small', to: 'math.ops.add-sub-100' },
  { from: 'math.number.place-value', to: 'math.ops.add-sub-100' },
  { from: 'math.ops.add-sub-100', to: 'math.ops.multiplication' },
  { from: 'math.ops.multiplication', to: 'math.ops.division' },
  { from: 'math.ops.multiplication', to: 'math.ops.ratio' },
  { from: 'math.ops.division', to: 'math.ops.ratio' },
  // Math: fractions
  { from: 'math.number.compare', to: 'math.fractions.halves-quarters' },
  { from: 'math.fractions.halves-quarters', to: 'math.fractions.equivalent' },
  { from: 'math.ops.division', to: 'math.fractions.equivalent' },
  { from: 'math.fractions.equivalent', to: 'math.fractions.decimals' },
  { from: 'math.number.place-value', to: 'math.fractions.decimals' },
  // Math: geometry / measurement
  { from: 'math.geom.shapes', to: 'math.geom.solids' },
  { from: 'math.geom.shapes', to: 'math.geom.symmetry' },
  { from: 'math.geom.solids', to: 'math.geom.area-perimeter' },
  { from: 'math.measure.length', to: 'math.geom.area-perimeter' },
  { from: 'math.ops.multiplication', to: 'math.geom.area-perimeter' },
  { from: 'math.number.count-20', to: 'math.measure.length' },
  { from: 'math.number.count-100', to: 'math.measure.time' },
  // Math: data / patterns
  { from: 'math.data.sort-classify', to: 'math.data.tally-graph' },
  { from: 'math.number.count-20', to: 'math.data.tally-graph' },
  { from: 'math.data.tally-graph', to: 'math.data.statistics' },
  { from: 'math.ops.division', to: 'math.data.statistics' },
  { from: 'math.patterns.basic', to: 'math.patterns.function' },
  { from: 'math.ops.multiplication', to: 'math.patterns.function' },

  // Literacy: phonological → decoding → fluency
  { from: 'lit.phono.rhyme', to: 'lit.phono.initial-sounds' },
  { from: 'lit.phono.initial-sounds', to: 'lit.phono.blend-segment' },
  { from: 'lit.decode.letters', to: 'lit.decode.letter-sounds' },
  { from: 'lit.phono.blend-segment', to: 'lit.decode.cvc' },
  { from: 'lit.decode.letter-sounds', to: 'lit.decode.cvc' },
  { from: 'lit.decode.cvc', to: 'lit.decode.sight-words' },
  { from: 'lit.decode.sight-words', to: 'lit.decode.fluency' },
  { from: 'lit.decode.cvc', to: 'lit.decode.fluency' },
  // Literacy: comprehension
  { from: 'lit.decode.fluency', to: 'lit.comp.main-idea' },
  { from: 'lit.comp.retell', to: 'lit.comp.main-idea' },
  { from: 'lit.comp.main-idea', to: 'lit.comp.inference' },
  // Literacy: writing
  { from: 'lit.decode.letters', to: 'lit.write.letters' },
  { from: 'lit.write.letters', to: 'lit.write.sentences' },
  { from: 'lit.decode.sight-words', to: 'lit.write.sentences' },
  { from: 'lit.write.sentences', to: 'lit.write.paragraph' },
  { from: 'lit.write.paragraph', to: 'lit.write.narrative' },
  { from: 'lit.write.paragraph', to: 'lit.write.informational' },
  { from: 'lit.vocab.descriptive', to: 'lit.write.narrative' },
  { from: 'lit.write.sentences', to: 'lit.vocab.descriptive' },
  { from: 'lit.comp.inference', to: 'lit.write.informational' },

  // Science inquiry
  { from: 'sci.inq.observe', to: 'sci.inq.predict' },
  { from: 'sci.inq.observe', to: 'sci.inq.record' },
  { from: 'sci.inq.predict', to: 'sci.inq.variable' },
  { from: 'sci.inq.record', to: 'sci.inq.variable' },
  { from: 'sci.inq.variable', to: 'sci.inq.analyze' },
  { from: 'math.data.tally-graph', to: 'sci.inq.analyze' },
  // Science content → inquiry prereqs
  { from: 'sci.inq.observe', to: 'sci.phys.reactions' },
  { from: 'sci.phys.reactions', to: 'sci.phys.states-of-matter' },
  { from: 'sci.phys.states-of-matter', to: 'sci.phys.forces' },
  { from: 'sci.phys.forces', to: 'sci.phys.energy' },
  { from: 'sci.life.living-things', to: 'sci.life.plants' },
  { from: 'sci.life.living-things', to: 'sci.life.animals' },
  { from: 'sci.life.plants', to: 'sci.life.ecosystems' },
  { from: 'sci.life.animals', to: 'sci.life.ecosystems' },
  { from: 'sci.earth.weather', to: 'sci.earth.water-cycle' },
  { from: 'sci.earth.rocks-geology', to: 'sci.space.solar-system' },

  // Language
  { from: 'lang.vocab.basic', to: 'lang.vocab.categorize' },
  { from: 'lang.vocab.categorize', to: 'lang.grammar.sentence' },
  { from: 'lit.write.sentences', to: 'lang.grammar.sentence' },

  // Coding
  { from: 'code.seq.instructions', to: 'code.debug' },
  { from: 'code.seq.instructions', to: 'code.seq.algorithms' },
  { from: 'code.seq.algorithms', to: 'code.loops' },
  { from: 'code.seq.algorithms', to: 'code.conditionals' },
  { from: 'code.loops', to: 'code.variables' },
  { from: 'code.conditionals', to: 'code.variables' },
  { from: 'code.variables', to: 'code.functions' },
  { from: 'code.seq.algorithms', to: 'code.functions' },

  // Engineering
  { from: 'eng.design.build', to: 'eng.design.plan' },
  { from: 'eng.design.build', to: 'eng.design.iterate' },
  { from: 'eng.design.plan', to: 'eng.design.process' },
  { from: 'eng.design.iterate', to: 'eng.design.process' },
  { from: 'eng.design.process', to: 'eng.design.constraints' },

  // Financial
  { from: 'fin.money.items-cost', to: 'fin.money.coins' },
  { from: 'math.number.count-10', to: 'fin.money.coins' },
  { from: 'fin.money.coins', to: 'fin.money.change' },
  { from: 'math.ops.add-sub-small', to: 'fin.money.change' },
  { from: 'fin.money.change', to: 'fin.money.budget' },
  { from: 'fin.money.budget', to: 'fin.save-invest' },
  { from: 'fin.money.coins', to: 'fin.entrepreneur' },
  { from: 'fin.entrepreneur', to: 'fin.money.budget' },

  // EQ
  { from: 'eq.emotions.name', to: 'eq.emotions.nuance' },
  { from: 'eq.emotions.name', to: 'eq.regulate.calm' },
  { from: 'eq.emotions.name', to: 'eq.social.empathy' },
  { from: 'eq.social.empathy', to: 'eq.social.conflict' },
  { from: 'eq.regulate.calm', to: 'eq.social.conflict' },

  // Creative / visual arts
  { from: 'art.motor.fine', to: 'art.represent' },
  { from: 'art.represent', to: 'art.project.plan' },
  { from: 'art.composition', to: 'art.project.plan' },

  // Social studies
  { from: 'ss.maps.basic', to: 'ss.maps.features' },
  { from: 'ss.maps.features', to: 'ss.maps.scale' },
  { from: 'math.measure.length', to: 'ss.maps.scale' },
  { from: 'ss.community', to: 'ss.culture' },
  { from: 'ss.history.timeline', to: 'ss.geography.regions' },
  { from: 'ss.community', to: 'ss.econ.trade' },
  { from: 'fin.money.items-cost', to: 'ss.econ.trade' },

  // Physical / body
  { from: 'phys.motor.locomotor', to: 'phys.motor.coordination' },
  { from: 'phys.motor.coordination', to: 'phys.fitness.endurance' },
  { from: 'body.anatomy.parts', to: 'body.anatomy.systems' },
  { from: 'body.hygiene', to: 'body.nutrition' },

  // Entrepreneurship subtree (Entrepreneur Fundamentals course)
  { from: 'fin.money.coins', to: 'fin.ent.wants-vs-needs' },
  { from: 'fin.ent.wants-vs-needs', to: 'fin.ent.barter' },
  { from: 'fin.ent.barter', to: 'fin.ent.value' },
  { from: 'fin.ent.value', to: 'fin.ent.pricing' },
  { from: 'fin.ent.pricing', to: 'fin.ent.business-plan' },
  { from: 'fin.entrepreneur', to: 'fin.ent.business-plan' },
  { from: 'fin.ent.business-plan', to: 'fin.ent.customer-service' },
  { from: 'fin.ent.business-plan', to: 'fin.ent.reliability' },
  { from: 'fin.ent.customer-service', to: 'fin.ent.market-research' },
  { from: 'fin.ent.reliability', to: 'fin.ent.services-pricing' },
  { from: 'fin.ent.services-pricing', to: 'fin.ent.diversification' },
  { from: 'fin.ent.business-plan', to: 'fin.ent.trades-careers' },
  { from: 'fin.ent.business-plan', to: 'fin.ent.bookkeeping' },
  { from: 'fin.ent.bookkeeping', to: 'fin.ent.scale' },
  { from: 'fin.ent.scale', to: 'fin.ent.delegation' },
  { from: 'fin.ent.market-research', to: 'fin.ent.problem-spotting' },
  { from: 'fin.ent.problem-spotting', to: 'fin.ent.platform-skepticism' },
  { from: 'fin.ent.delegation', to: 'fin.ent.pitch' },
  { from: 'fin.ent.platform-skepticism', to: 'fin.ent.pitch' },
];

// ─── Export graph ────────────────────────────────────────────────────────────

export const skillGraph: SkillGraph = {
  nodes: NODES,
  edges: EDGES,
  // Populated by `buildActivityToSkills` at runtime. Seeding an empty object
  // keeps the module side-effect free for tests that don't need the mapping.
  activityToSkills: {},
};

// ─── Auto-tagger ─────────────────────────────────────────────────────────────
// Maps an activity's learningObjectives to skill ids via keyword match.
// This avoids hand-labeling every activity while still producing deterministic
// coverage. When adding a new activity, its objectives automatically get
// tagged as long as they reuse vocabulary from NODES[].keywords.

export interface TaggableActivity {
  id: string;
  subject: string;
  ageVariants?: Record<
    string,
    { learningObjectives?: string[] } | undefined
  >;
}

/**
 * Return the skill ids that an activity builds, based on its learningObjectives.
 *
 * Matching rules:
 *   • Keyword matches are case-insensitive substring checks.
 *   • A skill is only considered if (a) its subject equals the activity subject,
 *     OR (b) there's no subject-matching node that matched (cross-subject fallback).
 *   • Duplicate skills across tiers/variants are de-duplicated.
 */
export function tagActivity(activity: TaggableActivity): string[] {
  const matched = new Set<string>();
  const variants = activity.ageVariants ?? {};
  const allObjectives: string[] = [];
  for (const v of Object.values(variants)) {
    if (v?.learningObjectives) allObjectives.push(...v.learningObjectives);
  }
  if (allObjectives.length === 0) return [];

  const haystack = allObjectives.join(' \n ').toLowerCase();

  // First pass: same-subject matches.
  for (const node of NODES) {
    if (node.subject !== activity.subject) continue;
    for (const kw of node.keywords) {
      if (haystack.includes(kw.toLowerCase())) {
        matched.add(node.id);
        break;
      }
    }
  }

  // Second pass: cross-subject matches (only adds if none from same subject,
  // OR if the keyword is an unusually strong signal like "compound interest").
  if (matched.size === 0) {
    for (const node of NODES) {
      for (const kw of node.keywords) {
        if (haystack.includes(kw.toLowerCase())) {
          matched.add(node.id);
          break;
        }
      }
    }
  }

  return Array.from(matched);
}

/**
 * Build the activityToSkills map for a list of activities and mutate the
 * shared skillGraph in place. Call once at app startup.
 */
export function buildActivityToSkills(
  activities: TaggableActivity[],
): Record<string, string[]> {
  const map: Record<string, string[]> = {};
  for (const a of activities) {
    const skills = tagActivity(a);
    if (skills.length > 0) map[a.id] = skills;
  }
  skillGraph.activityToSkills = map;
  return map;
}

// ─── Indexes (lazy) ──────────────────────────────────────────────────────────

let _outgoing: Map<string, string[]> | null = null;
let _incoming: Map<string, string[]> | null = null;

function buildIndexes(): void {
  _outgoing = new Map();
  _incoming = new Map();
  for (const n of NODES) {
    _outgoing.set(n.id, []);
    _incoming.set(n.id, []);
  }
  for (const e of EDGES) {
    _outgoing.get(e.from)?.push(e.to);
    _incoming.get(e.to)?.push(e.from);
  }
}

function outgoing(id: string): string[] {
  if (!_outgoing) buildIndexes();
  return _outgoing!.get(id) ?? [];
}

function incoming(id: string): string[] {
  if (!_incoming) buildIndexes();
  return _incoming!.get(id) ?? [];
}

// ─── Helper functions ────────────────────────────────────────────────────────

/**
 * Given a set of completed activity ids, return the activities that should be
 * recommended next: activities whose tagged skills all have their prerequisites
 * satisfied by skills the learner has already built.
 *
 * Mastery model: a skill is "mastered" if any completed activity was tagged
 * with it. This is intentionally permissive for ages 3–10 — we want low
 * friction — but consumers can pass an explicit `masteredSkills` override.
 */
export function getNextActivities(
  completedActivityIds: string[],
  opts?: { masteredSkills?: Set<string>; limit?: number },
): string[] {
  const mastered =
    opts?.masteredSkills ?? skillsFromCompletedActivities(completedActivityIds);
  const completed = new Set(completedActivityIds);
  const candidates: string[] = [];

  for (const [activityId, skillIds] of Object.entries(
    skillGraph.activityToSkills,
  )) {
    if (completed.has(activityId)) continue;
    const ready = skillIds.every((sid) => prerequisitesMet(sid, mastered));
    // Must also introduce at least one unmastered skill — no point repeating
    // material the learner already has.
    const stretches = skillIds.some((sid) => !mastered.has(sid));
    if (ready && stretches) candidates.push(activityId);
  }

  const limit = opts?.limit ?? 20;
  return candidates.slice(0, limit);
}

/**
 * Back-chain from a goal skill to a topologically ordered list of skill ids
 * the learner needs to reach it.
 */
export function getSkillPath(goalSkillId: string): string[] {
  const visited = new Set<string>();
  const order: string[] = [];

  function visit(id: string): void {
    if (visited.has(id)) return;
    visited.add(id);
    for (const prereq of incoming(id)) visit(prereq);
    order.push(id);
  }

  if (!NODES.find((n) => n.id === goalSkillId)) return [];
  visit(goalSkillId);
  return order;
}

/**
 * Return activity ids whose prerequisites are all covered by `masteredSkills`.
 * Unlike `getNextActivities`, this does NOT require the activity to add a new
 * skill — useful for review / free-choice browsing.
 */
export function getUnlockedActivities(
  masteredSkills: Set<string>,
): string[] {
  const out: string[] = [];
  for (const [activityId, skillIds] of Object.entries(
    skillGraph.activityToSkills,
  )) {
    if (skillIds.every((sid) => prerequisitesMet(sid, masteredSkills))) {
      out.push(activityId);
    }
  }
  return out;
}

// ─── Internal helpers ────────────────────────────────────────────────────────

function prerequisitesMet(
  skillId: string,
  masteredSkills: Set<string>,
): boolean {
  for (const prereq of incoming(skillId)) {
    if (!masteredSkills.has(prereq)) return false;
  }
  return true;
}

function skillsFromCompletedActivities(
  completedActivityIds: string[],
): Set<string> {
  const out = new Set<string>();
  for (const id of completedActivityIds) {
    for (const sid of skillGraph.activityToSkills[id] ?? []) out.add(sid);
  }
  return out;
}

// ─── Debug / introspection ──────────────────────────────────────────────────

export function graphStats(): {
  nodeCount: number;
  edgeCount: number;
  nodesBySubject: Record<string, number>;
  taggedActivities: number;
} {
  const nodesBySubject: Record<string, number> = {};
  for (const n of NODES) {
    nodesBySubject[n.subject] = (nodesBySubject[n.subject] ?? 0) + 1;
  }
  return {
    nodeCount: NODES.length,
    edgeCount: EDGES.length,
    nodesBySubject,
    taggedActivities: Object.keys(skillGraph.activityToSkills).length,
  };
}
